import React, { useState, useContext, useEffect } from 'react';
import {
  BankOutlined,
  HomeOutlined,
  StarOutlined,
  EnvironmentOutlined,
  WarningOutlined,
  CameraOutlined,
} from '@ant-design/icons';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import dayjs from "dayjs";

const user_avatar = "https://api.dicebear.com/7.x/thumbs/svg?seed=user";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    font-family: 'DM Sans', sans-serif;
    background: #f0f2f5;
    min-height: 100vh;
    padding: 32px 16px;
  }

  .log-wrapper {
    max-width: 780px;
    margin: 0 auto;
  }

  .log-header {
    margin-bottom: 32px;
  }

  .log-header h2 {
    font-size: 22px;
    font-weight: 600;
    color: #1a1a2e;
    letter-spacing: -0.3px;
  }

  .log-header p {
    font-size: 13px;
    color: #888;
    margin-top: 4px;
    font-family: 'DM Mono', monospace;
  }

  .timeline {
    position: relative;
    padding-left: 0;
  }

  .timeline::before {
    display: none;
  }

  .timeline-item {
    position: relative;
    margin-bottom: 14px;
    animation: fadeUp 0.4s ease both;
  }

  .timeline-item:nth-child(1) { animation-delay: 0.05s; }
  .timeline-item:nth-child(2) { animation-delay: 0.15s; }
  .timeline-item:nth-child(3) { animation-delay: 0.25s; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .dot {
    display: none;
  }

  .card {
    background: #ffffff;
    border-radius: 16px;
    padding: 16px 18px;
    box-shadow: 0 4px 16px rgba(15,23,42,0.04);
    border: 1px solid #e2e8f0;
    transition: box-shadow 0.2s;
  }

  .card:hover {
    box-shadow: 0 4px 20px rgba(0,0,0,0.09);
  }

  .card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
  }

  .location-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #1a1a2e;
  }

  .location-icon {
    width: 26px;
    height: 26px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .type-tag {
    font-size: 11px;
    font-weight: 500;
    font-family: 'DM Mono', monospace;
    padding: 3px 10px;
    border-radius: 20px;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }

  .type-tag.check { background: #eef0ff; color: #0e497b; }
  .type-tag.punch { background: #f3eeff; color: #0e497b; }

  .times-row {
    display: flex;
    gap: 12px;
    margin-top: 8px;
    margin-bottom: 12px;
  }

  .time-block {
    flex: 1;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 10px 14px;
  }

  .time-label {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    margin-bottom: 2px;
  }

  .time-value {
    font-size: 17px;
    font-weight: 800;
    color: #0f172a;
    font-family: 'DM Mono', monospace;
  }

  .time-value.missing {
    color: #f59e0b;
    font-size: 12px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .photos-section {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .photos-section-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #aaa;
    margin-right: 2px;
  }

  .photo-block {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e8e8e8;
    width: 100px;
    height: 100px;
    flex-shrink: 0;
  }

  .photo-block img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .photo-label {
    position: absolute;
    bottom: 3px;
    left: 3px;
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    background: rgba(0,0,0,0.5);
    color: white;
    padding: 1px 5px;
    border-radius: 20px;
    line-height: 1.6;
  }

  .no-photo {
    width: 48px;
    height: 48px;
    background: #fafafa;
    border: 1px dashed #ddd;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    color: #bbb;
    font-size: 9px;
    flex-shrink: 0;
  }

  .punch-divider {
    height: 1px;
    background: #f0f0f0;
    margin: 12px 0;
  }
`;

function getEntryType(punch) {
  if (punch?.in_out_mark === 0 || punch?.in_out_mark === "0" || punch?.in_out_mark === 1 || punch?.in_out_mark === "1") {
    return 'check';
  }
  return punch?.PunchIn || punch?.PunchOut ? 'punch' : 'check';
}

function getInTime(punch, type) {
  if (punch?.in_out_mark === 1 || punch?.in_out_mark === "1") {
    return punch?.CheckIn || punch?.PunchIn || punch?.Time || punch?.time || punch?.sort_time;
  }
  return type === "punch" ? punch?.PunchIn : punch?.CheckIn;
}

function getOutTime(punch, type) {
  if (punch?.in_out_mark === 0 || punch?.in_out_mark === "0") {
    return punch?.CheckOut || punch?.PunchOut || punch?.Time || punch?.time || punch?.sort_time;
  }
  return type === "punch" ? punch?.PunchOut : punch?.CheckOut;
}

const locationConfig = {
  "Head Office": { Icon: BankOutlined, bg: "#eef0ff", color: "#0e497b" },
  "Crown Residencies": { Icon: HomeOutlined, bg: "#f3eeff", color: "#0e497b" },
  "Enchanted": { Icon: StarOutlined, bg: "#fff7e6", color: "#0e497b" },
};


const parseTimeStringToSeconds = (str) => {
  if (!str) return null;
  let s = String(str).trim();
  const isPM = /pm/i.test(s);
  const isAM = /am/i.test(s);
  s = s.replace(/(am|pm)/i, "").trim();
  const parts = s.split(":").map(Number);
  if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;

  let hours = parts[0];
  const minutes = parts[1];
  const seconds = parts[2] || 0;

  if (isPM && hours < 12) hours += 12;
  if (isAM && hours === 12) hours = 0;

  return hours * 3600 + minutes * 60 + seconds;
};

const calculateTotalHours = (inTimeStr, outTimeStr) => {
  if (!inTimeStr || !outTimeStr) return null;
  const startSec = parseTimeStringToSeconds(inTimeStr);
  let endSec = parseTimeStringToSeconds(outTimeStr);
  if (startSec === null || endSec === null) return null;

  // Handle overnight / I-Shift punch out across midnight (e.g. 22:00 to 06:30)
  if (endSec < startSec) {
    endSec += 24 * 3600;
  }

  const diffSec = endSec - startSec;
  const hrs = Math.floor(diffSec / 3600);
  const mins = Math.floor((diffSec % 3600) / 60);
  const secs = diffSec % 60;
  return `${hrs}h ${mins}m ${secs}s`;
};

const calculateBridgeTime = (prevOutTimeStr, nextInTimeStr) => {
  if (!prevOutTimeStr || !nextInTimeStr) return null;
  const startSec = parseTimeStringToSeconds(prevOutTimeStr);
  let endSec = parseTimeStringToSeconds(nextInTimeStr);
  if (startSec === null || endSec === null) return null;

  if (endSec < startSec) {
    endSec += 24 * 3600;
  }

  const diffSec = endSec - startSec;
  if (diffSec <= 0) return null;

  const hrs = Math.floor(diffSec / 3600);
  const mins = Math.floor((diffSec % 3600) / 60);
  const secs = diffSec % 60;

  if (hrs > 0) return `${hrs}h ${mins}m ${secs}s`;
  return `${mins}m ${secs}s`;
};

const getProcessedPunchDetails = (attendanceData, targetDate, empId, employeeObj) => {
  if (!targetDate) return [];
  const targetEntries = (attendanceData || []).filter(e => {
    const matchDate = (!targetDate || e.Date === targetDate || e.date === targetDate);
    if (!matchDate) return false;

    // If attendanceData was returned from a date & employee specific API request (<= 5 items), accept it
    if (attendanceData && attendanceData.length > 0 && attendanceData.length <= 5) {
      return true;
    }

    if (!empId) return true;

    const eEmpId = String(e.Emp_id || e.employee_id || e.employee || e.id || "");
    if (eEmpId && (eEmpId === String(empId) || eEmpId === String(employeeObj?.id))) return true;

    const eCode = String(e.EmployeeCode || e.emp_code || e.employee_code || "").toLowerCase();
    const targetCode = String(employeeObj?.emp_code || employeeObj?.EmployeeCode || employeeObj?.employee_code || empId || "").toLowerCase();
    if (eCode && targetCode && eCode === targetCode) return true;

    return true;
  });

  let list = [];

  targetEntries.forEach((entry) => {
    const punchDetails = entry?.punch_details || [];
    if (punchDetails.length > 0) {
      punchDetails.forEach((punch) => {
        let copy = { ...punch };
        const type = getEntryType(copy);
        const inTime = getInTime(copy, type);
        let outTime = getOutTime(copy, type);

        // Disambiguate when PunchIn === PunchOut
        if (copy.PunchIn && copy.PunchOut && copy.PunchIn === copy.PunchOut) {
          if (entry.CheckIn || (entry.FirstInTime && parseTimeStringToSeconds(entry.FirstInTime) < parseTimeStringToSeconds(copy.PunchIn))) {
            // Evening punch after morning CheckIn -> It is a PunchOut!
            copy.PunchIn = null;
          } else {
            // Morning single punch -> It is a PunchIn!
            copy.PunchOut = null;
          }
        }

        // Explicitly check in_out_mark site office flag (1 = IN, 0 = OUT)
        if (copy.in_out_mark === 1 || copy.in_out_mark === "1") {
          copy.CheckIn = inTime;
          copy.CheckOut = null;
        } else if (copy.in_out_mark === 0 || copy.in_out_mark === "0") {
          copy.CheckOut = outTime;
          copy.CheckIn = null;
        } else if (copy.RecordType === "BIOMETRIC" || copy.PunchIn || copy.PunchOut) {
          const pTime = copy.PunchIn || copy.PunchOut;
          // If this biometric timestamp is equal to LastOutTime or LastOutType is Punch-Out, treat as PunchOut
          if (pTime && (pTime === entry.LastOutTime || entry.LastOutType === "Punch-Out" || entry.LastOutType === "PUNCH-OUT")) {
            copy.PunchOut = pTime;
            copy.PunchIn = null;
          }
        }

        list.push({ entry, punch: copy });
      });
    } else {
      // Synthetic fallback for top-level CheckIn / PunchIn fields from AttendanceSiteOffice payload
      if (entry.CheckIn || entry.CheckOut) {
        list.push({
          entry,
          punch: {
            RecordType: "MANUAL/APP",
            CheckIn: entry.CheckIn,
            CheckOut: entry.CheckOut,
            check_in_work_site: entry.CheckInWorkSite || entry.WorkLocation,
            check_out_work_site: entry.CheckOutWorkSite || entry.WorkLocation,
            check_in_photo_url: entry.check_in_photo_url,
            check_out_photo_url: entry.check_out_photo_url
          }
        });
      }
      if (entry.PunchIn || entry.PunchOut) {
        const isLastOut = (entry.LastOutType === "Punch-Out" || entry.LastOutType === "PUNCH-OUT" || entry.PunchOut === entry.LastOutTime);
        list.push({
          entry,
          punch: {
            RecordType: "BIOMETRIC",
            PunchIn: isLastOut ? null : entry.PunchIn,
            PunchOut: isLastOut ? (entry.PunchOut || entry.LastOutTime) : null,
            Location: entry.WorkLocation || "Site Office"
          }
        });
      }
    }
  });

  return list;
};

const calculateDayTotalHours = (attendanceData, targetDate, empId, fallbackRecord) => {
  if (!targetDate) return "-";

  const dataToUse = (attendanceData && attendanceData.length > 0) ? attendanceData : (fallbackRecord ? [fallbackRecord] : []);
  const entry = (attendanceData || []).find(e => (e.Date === targetDate || e.date === targetDate)) || fallbackRecord;

  // 1. Calculate Net Worked Time by merging all overlapping sessions (e.g. Biometric & Mobile Check-in)
  const processedList = getProcessedPunchDetails(dataToUse, targetDate, empId, fallbackRecord);
  let rawIntervals = [];
  let hasValidSession = false;
  let hasActiveCheckInWithoutCheckOut = false;

  processedList.forEach(({ punch }) => {
    const type = getEntryType(punch);
    const inTimeStr = getInTime(punch, type);
    const outTimeStr = getOutTime(punch, type);

    if (inTimeStr && outTimeStr && inTimeStr !== outTimeStr) {
      const startSec = parseTimeStringToSeconds(inTimeStr);
      let endSec = parseTimeStringToSeconds(outTimeStr);
      if (startSec !== null && endSec !== null) {
        if (endSec < startSec) {
          endSec += 24 * 3600;
        }
        rawIntervals.push([startSec, endSec]);
        hasValidSession = true;
      }
    } else if (inTimeStr && !outTimeStr) {
      hasActiveCheckInWithoutCheckOut = true;
    }
  });

  if (hasValidSession && rawIntervals.length > 0) {
    // Sort intervals by start time
    rawIntervals.sort((a, b) => a[0] - b[0]);

    // Merge overlapping or contiguous intervals so overlapping punches/check-ins are not double counted
    const mergedIntervals = [];
    for (let current of rawIntervals) {
      if (!mergedIntervals.length) {
        mergedIntervals.push([...current]);
      } else {
        const prev = mergedIntervals[mergedIntervals.length - 1];
        if (current[0] <= prev[1]) {
          prev[1] = Math.max(prev[1], current[1]);
        } else {
          mergedIntervals.push([...current]);
        }
      }
    }

    let totalSeconds = 0;
    for (let [start, end] of mergedIntervals) {
      totalSeconds += (end - start);
    }

    if (totalSeconds > 0) {
      const hrs = Math.floor(totalSeconds / 3600);
      const mins = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;
      return `${hrs}h ${mins}m ${secs}s`;
    }
  }

  const todayStr = dayjs().format("YYYY-MM-DD");
  if (targetDate === todayStr && (hasActiveCheckInWithoutCheckOut || (entry?.FirstInTime && !entry?.LastOutTime))) {
    return "In Progress";
  }

  // 2. Fallback to backend pre-calculated TotalTime / OfficeWorkingHours / total_time
  const backendTime = entry?.TotalTime || entry?.OfficeWorkingHours || entry?.total_time;
  if (backendTime && backendTime !== "0 hours, 0 minutes" && backendTime !== "0h 0m" && backendTime !== "00:00:00") {
    const timeStr = String(backendTime).trim();
    if (timeStr === "In Progress" || timeStr === "inprocess") {
      if (targetDate === todayStr) return "In Progress";
      return "-";
    }
    if (timeStr === "-" || timeStr === " - ") {
      return "-";
    }
    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(timeStr)) {
      const parts = timeStr.split(":");
      const hrs = parseInt(parts[0], 10);
      const mins = parseInt(parts[1], 10);
      const secs = parts[2] ? parseInt(parts[2], 10) : 0;
      if (hrs === 0 && mins === 0 && secs === 0) return "-";
      return `${hrs}h ${mins}m ${secs}s`;
    }
    return timeStr
      .replace(/hours?/gi, "h")
      .replace(/minutes?/gi, "m")
      .replace(/,/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  return "-";
};

export default function ImproperLog({ record, employee }) {
  const [attendanceData, setAttendanceData] = useState([]);
  const { authData, backUrl } = useContext(AuthContext);
  const [showLead, setShowLead] = useState(false);
  const [selectedLeadDetails, setSelectedLeadDetails] = useState(null);
  // Stores resolved location names keyed by "lat,lng"
  const [locationNames, setLocationNames] = useState({});

  const today = dayjs().format("YYYY-MM-DD");

  console.log("RECORD  :", record)
  console.log("Employee :", employee)

  const fetchData = async () => {
    const targetDate = record?.Date || record?.date;
    const empId = employee?.id || record?.Emp_id || record?.employee_id || record?.employee;
    if (!targetDate) return;

    try {
      const params = new URLSearchParams({ date: targetDate });
      if (empId) params.append('employee_id', empId);

      const res = await axios.get(
        `${backUrl}/api/hr/attendance-comparsion/?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${authData?.access}`,
          },
        }
      );
      const resData = Array.isArray(res.data) ? res.data : (res.data.results || []);
      setAttendanceData(resData);
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      setAttendanceData([]);
    }
  };

  // Reverse-geocode coordinates to a human-readable location name
  const fetchLocationName = async (lat, lng) => {
    const key = `${lat},${lng}`;
    if (locationNames[key]) return; // already fetched
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=en`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await res.json();
      // Use suburb/neighbourhood/town for a short name, fallback to full display_name
      const addr = data.address || {};
      const shortName =
        addr.suburb ||
        addr.neighbourhood ||
        addr.village ||
        addr.town ||
        addr.city ||
        addr.county ||
        data.display_name ||
        `${lat}, ${lng}`;
      setLocationNames((prev) => ({ ...prev, [key]: shortName }));
    } catch {
      setLocationNames((prev) => ({ ...prev, [key]: `${lat}, ${lng}` }));
    }
  };

  useEffect(() => {
    if (record?.Date || record?.date) {
      fetchData();
    }
  }, [record?.Date, record?.date, employee?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch location names for all auto-checkout entries after data loads
  useEffect(() => {
    attendanceData.forEach((entry) => {
      const lat = entry?.sales_checkout_latitude;
      const lng = entry?.sales_checkout_longitude;
      if (lat && lng) fetchLocationName(lat, lng);

      const punchDetails = entry?.punch_details || [];
      punchDetails.forEach((punch) => {
        const plat = punch?.sales_checkout_latitude;
        const plng = punch?.sales_checkout_longitude;
        if (plat && plng) fetchLocationName(plat, plng);
      });
    });
  }, [attendanceData]); // eslint-disable-line react-hooks/exhaustive-deps

  const targetDate = record?.Date || record?.date;
  const empId = employee?.id || record?.Emp_id || record?.employee_id || record?.employee || employee?.emp_code || record?.EmployeeCode;
  const dataToUse = (attendanceData && attendanceData.length > 0) ? attendanceData : (record ? [record] : []);
  const processedPunches = getProcessedPunchDetails(dataToUse, targetDate, empId, employee);

  return (
    <>
      <style>{styles}</style>
      <div className="log-wrapper">
        {/* COMPACT TOTAL WORKING HOURS PILL */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <span
            style={{
              background: "linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)",
              border: "1.5px solid #38bdf8",
              borderRadius: 10,
              padding: "5px 14px",
              fontSize: 13,
              fontWeight: 800,
              color: "#0369a1",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              boxShadow: "0 2px 8px rgba(3, 105, 161, 0.12)"
            }}
          >
            <span style={{ fontSize: 14 }}>⏱️</span>
            <span>Total Hours: {calculateDayTotalHours(attendanceData, targetDate, empId, record)}</span>
          </span>
        </div>

        <div className="timeline">
          {processedPunches.length > 0 ? (
            processedPunches.map(({ entry, punch }, punchIdx) => {
              const type = getEntryType(punch);
              const inTime = getInTime(punch, type);
              const outTime = getOutTime(punch, type);
              const locConf = locationConfig[entry.WorkLocation] || { Icon: EnvironmentOutlined, bg: "#f0f0f0", color: "#888" };
              const { Icon } = locConf;
              const autoCheckoutRemarks = punch?.sales_checkout_remarks || null;
              const autoLat = punch?.sales_checkout_latitude || null;
              const autoLng = punch?.sales_checkout_longitude || null;

              return (
                <div className="timeline-item" key={`punch-${punchIdx}`}>
                  <div className={`dot ${type}`} />
                  <div className="card">
                    {/* Location Header */}
                    <div className="card-top" style={{ marginBottom: 12 }}>
                      <div className="location-badge" style={{ fontSize: 12.5, fontWeight: 700, color: "#334155", display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 14 }}>📍</span>
                        <span>
                          {punch.check_in_work_site && punch.check_out_work_site ? (
                            `${punch.check_in_work_site} ➔ ${punch.check_out_work_site}`
                          ) : punch.check_in_work_site ? (
                            punch.check_in_work_site
                          ) : punch.check_out_work_site ? (
                            punch.check_out_work_site
                          ) : (entry.WorkLocation || "Head Office")}
                        </span>
                      </div>
                    </div>

                    {(inTime || outTime) && (
                      <div style={{ marginTop: 8, marginBottom: 16 }}>
                        {/* PUNCH IN (Green Card) <--- Connecting Line + Session Duration Badge ---> PUNCH OUT Card */}
                        <div style={{ display: "flex", alignItems: "center", gap: 0, position: "relative" }}>
                          {/* GREEN PUNCH IN CARD */}
                          <div
                            style={{
                              flex: 1,
                              background: inTime ? "#f0fdf4" : "#fff7ed",
                              border: inTime ? "1.5px solid #bbf7d0" : "1.5px solid #fed7aa",
                              borderRadius: 14,
                              padding: "14px 18px",
                              boxShadow: inTime ? "0 2px 8px rgba(16, 185, 129, 0.06)" : "0 2px 8px rgba(234, 88, 12, 0.06)",
                              zIndex: 2,
                            }}
                          >
                            <div style={{ fontSize: 11, fontWeight: 800, color: inTime ? "#047857" : "#c2410c", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
                              <span style={{ width: 8, height: 8, borderRadius: "50%", background: inTime ? "#10b981" : "#f97316", display: "inline-block" }} />
                              {type === 'check' ? 'CHECK IN' : 'PUNCH IN'}
                            </div>
                            {inTime ? (
                              <div style={{ fontSize: 19, fontWeight: 900, color: "#065f46", fontFamily: "'Inter', sans-serif" }}>
                                {inTime}
                              </div>
                            ) : (
                              <div style={{ fontSize: 14, fontWeight: 900, color: "#c2410c", fontFamily: "'Inter', sans-serif", letterSpacing: "0.4px" }}>
                                NO PUNCHIN
                              </div>
                            )}
                          </div>

                          {/* IN-BETWEEN TIMELINE LINE WITH SESSION DURATION BADGE */}
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minWidth: 120, margin: "0 -6px", zIndex: 3, position: "relative" }}>
                            {/* Horizontal Connecting Line */}
                            <div
                              style={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                height: 3,
                                background: (inTime && outTime) ? "linear-gradient(90deg, #10b981 0%, #0284c7 100%)" : "linear-gradient(90deg, #10b981 0%, #f97316 100%)",
                                zIndex: 1,
                              }}
                            />
                            {/* Session Duration Badge Pill in Center (Simple & Neutral) */}
                            <div
                              style={{
                                position: "relative",
                                zIndex: 2,
                                background: "#ffffff",
                                border: (inTime && outTime) ? "2px solid #64748b" : "2px solid #f97316",
                                borderRadius: 22,
                                padding: "5px 14px",
                                fontSize: 12.5,
                                fontWeight: 900,
                                color: (inTime && outTime) ? "#0f172a" : "#c2410c",
                                boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
                                whiteSpace: "nowrap",
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                              }}
                            >
                              <span>⏱️</span>
                              <span>{inTime && outTime ? (calculateTotalHours(inTime, outTime) || "--") : outTime ? "Punch Out" : "No Punch Out"}</span>
                            </div>
                          </div>

                          {/* SKY BLUE PUNCH OUT CARD */}
                          <div
                            style={{
                              flex: 1,
                              background: outTime ? "#f0f9ff" : "#fff7ed",
                              border: outTime ? "1.5px solid #bae6fd" : "1.5px solid #fed7aa",
                              borderRadius: 14,
                              padding: "14px 18px",
                              boxShadow: outTime ? "0 2px 8px rgba(2, 132, 199, 0.06)" : "0 2px 8px rgba(234, 88, 12, 0.06)",
                              zIndex: 2,
                            }}
                          >
                            <div style={{ fontSize: 11, fontWeight: 800, color: outTime ? "#0369a1" : "#c2410c", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
                              <span style={{ width: 8, height: 8, borderRadius: "50%", background: outTime ? "#0284c7" : "#f97316", display: "inline-block" }} />
                              {type === 'check' ? 'CHECK OUT' : 'PUNCH OUT'}
                            </div>
                            {outTime ? (
                              <div style={{ fontSize: 19, fontWeight: 900, color: "#0c4a6e", fontFamily: "'Inter', sans-serif" }}>
                                {outTime}
                              </div>
                            ) : (
                              <div style={{ fontSize: 14, fontWeight: 900, color: "#c2410c", fontFamily: "'Inter', sans-serif", letterSpacing: "0.4px" }}>
                                NO PUNCHOUT
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {(() => {
                      const raw = punch.lead_data || punch.lead_details || punch.external_lead_details;
                      if (!raw) return false;
                      try {
                        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                        if (Array.isArray(parsed)) return parsed.length > 0;
                        if (typeof parsed === "object" && parsed !== null) return Object.keys(parsed).length > 0;
                        return !!parsed;
                      } catch (e) {
                        return typeof raw === "string" && raw.trim() !== "" && raw !== "[]" && raw !== "{}";
                      }
                    })() && (
                        <div style={{ marginBottom: 14 }}>
                          <a
                            onClick={() => {
                              const raw = punch.lead_data || punch.lead_details || punch.external_lead_details;
                              let parsed = raw;
                              if (typeof raw === "string") { try { parsed = JSON.parse(raw); } catch { } }
                              const arr = Array.isArray(parsed) ? parsed : [parsed];
                              setSelectedLeadDetails(arr);
                              setShowLead(true);
                            }}
                            style={{
                              color: "#0e497b",
                              fontSize: "12px",
                              fontWeight: 600,
                              textDecoration: "underline",
                              cursor: "pointer"
                            }}
                          >
                            View Visit Lead Details
                          </a>
                        </div>
                      )}

                    {type === 'check' && (
                      <>
                        <div className="photos-section">
                          <span className="photos-section-label">Photos</span>

                          {punch.check_in_photo_url ? (
                            <div className="photo-block">
                              <img src={punch.check_in_photo_url} alt="Check-in" />
                              <span className="photo-label">In</span>
                            </div>
                          ) : (
                            <div className="no-photo">
                              <CameraOutlined style={{ fontSize: 13 }} />
                              <span>In</span>
                            </div>
                          )}

                          {punch.check_out_photo_url ? (
                            <div className="photo-block">
                              <img src={punch.check_out_photo_url} alt="Check-out" />
                              <span className="photo-label">Out</span>
                            </div>
                          ) : (
                            <div className="no-photo">
                              <CameraOutlined style={{ fontSize: 13 }} />
                              <span>Out</span>
                            </div>
                          )}
                        </div>

                        {/* Auto-Checkout Alert — only rendered when auto-checkout occurred */}
                        {autoCheckoutRemarks && (
                          <div style={{
                            marginTop: 12,
                            padding: "8px 12px",
                            background: "#fff7e6",
                            border: "1px solid #ffa940",
                            borderRadius: 8,
                            fontSize: 12,
                            color: "#d46b08",
                            fontWeight: 500,
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                          }}>
                            <span>⚠️ {autoCheckoutRemarks}</span>
                            {autoLat && autoLng && (
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${autoLat},${autoLng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#1677ff", fontSize: 11, marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}
                              >
                                <EnvironmentOutlined style={{ marginRight: 3 }} />
                                {locationNames[`${autoLat},${autoLng}`] || "Fetching location..."}
                              </a>
                            )}
                          </div>
                        )}
                      </>
                    )}


                  </div>
                </div>
              );
            })
          ) : (
            /* ELEGANT DAY STATUS CARD FOR NON-PUNCH DAYS */
            (() => {
              const dayEntry = (dataToUse && dataToUse[0]) || record || {};
              const statusRaw = String(dayEntry?.Status || dayEntry?.AttendanceStatus || dayEntry?.status || "").toUpperCase();
              const remarksRaw = dayEntry?.StatusMessage || dayEntry?.remarks || dayEntry?.HolidayName || dayEntry?.LeaveType || null;

              const isLeave = statusRaw.includes("LEAVE") || statusRaw.includes("CASUAL") || statusRaw.includes("SICK") || statusRaw.includes("LOP") || !!dayEntry?.LeaveType;
              const isHoliday = statusRaw.includes("HOLIDAY") || !!dayEntry?.HolidayName;
              const isWeekOff = statusRaw.includes("SUNDAY") || statusRaw.includes("WEEKOFF") || statusRaw.includes("WEEK OFF") || statusRaw === "WO";
              const isAbsent = statusRaw.includes("ABSENT");
              const isPermission = statusRaw.includes("PERMISSION");

              let cardBg = "#f8fafc";
              let cardBorder = "#e2e8f0";
              let icon = "ℹ️";
              let title = "No Punch Recorded";
              let subtitle = "No check-in or biometric punch logs for this date.";

              if (isLeave) {
                cardBg = "#fdf2f8";
                cardBorder = "#fbcfe8";
                icon = "🏖️";
                title = `Leave: ${dayEntry?.LeaveType || dayEntry?.AttendanceStatus || "Approved Leave"}`;
                subtitle = remarksRaw ? `Remarks: ${remarksRaw}` : "On approved leave. Work punches not required.";
              } else if (isHoliday) {
                cardBg = "#fff7ed";
                cardBorder = "#fed7aa";
                icon = "🎉";
                title = `Holiday: ${dayEntry?.HolidayName || remarksRaw || "Company Holiday"}`;
                subtitle = "Official company holiday. Work punches not required.";
              } else if (isWeekOff) {
                cardBg = "#fefce8";
                cardBorder = "#fef08a";
                icon = "🌴";
                title = "Weekly Off";
                subtitle = "Scheduled weekly off day.";
              } else if (isAbsent) {
                cardBg = "#fef2f2";
                cardBorder = "#fecaca";
                icon = "❌";
                title = "Absent";
                subtitle = "No check-in or biometric punch recorded on this work date.";
              } else if (isPermission) {
                cardBg = "#f0fdf4";
                cardBorder = "#bbf7d0";
                icon = "⏱️";
                title = "Permission Granted";
                subtitle = remarksRaw || "Approved work permission for this date.";
              }

              return (
                <div
                  style={{
                    background: cardBg,
                    border: `1.5px solid ${cardBorder}`,
                    borderRadius: 16,
                    padding: "24px 20px",
                    marginTop: 8,
                    textAlign: "center",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.03)"
                  }}
                  
                >
                  <div style={{ fontSize: 32, marginBottom: 8 }}>{icon}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", marginBottom: 4 }}>{title}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#64748b" }}>{subtitle}</div>
                </div>
              );
            })()

)}

        </div>
      </div>

      {showLead && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '600px',
            maxHeight: '80vh', overflowY: 'auto', padding: '24px', position: 'relative'
          }}>
            <button
              onClick={() => setShowLead(false)}
              style={{
                position: 'absolute', top: '16px', right: '16px', border: 'none',
                background: 'none', cursor: 'pointer', fontSize: '20px', color: '#888'
              }}
            >
              ×
            </button>
            <h3 style={{ marginBottom: '20px', color: '#1a1a2e' }}>Lead Details</h3>
            {selectedLeadDetails && selectedLeadDetails.map((lead, index) => (
              <div key={index} style={{
                marginBottom: '16px', padding: '16px', borderRadius: '8px',
                border: '1px solid #f0f0f0', backgroundColor: '#fafafa'
              }}>
                {selectedLeadDetails.length > 1 && <h4 style={{ marginBottom: '12px' }}>Lead {index + 1}</h4>}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', rowGap: '10px', fontSize: '14px' }}>
                  <div style={{ color: '#888' }}>Lead No:</div>
                  <div style={{ fontWeight: 600 }}>{lead.lead_no || lead.lead_id || "-"}</div>
                  <div style={{ color: '#888' }}>Customer:</div>
                  <div>{lead.customer_name || lead.name || "-"}</div>
                  <div style={{ color: '#888' }}>Email:</div>
                  <div>{lead.email || lead.customer_email || "-"}</div>
                  <div style={{ color: '#888' }}>Phone:</div>
                  <div>{lead.number || lead.phone || "-"}</div>
                  <div style={{ color: '#888' }}>Project:</div>
                  <div>{lead.project_name || lead.project_id || (lead.project && lead.project.title) || "-"}</div>
                  <div style={{ color: '#888' }}>Status:</div>
                  <div><span style={{
                    padding: '2px 8px', borderRadius: '4px', fontSize: '12px',
                    backgroundColor: '#eef0ff', color: '#0e497b', fontWeight: 500
                  }}>
                    {lead.status?.title || lead.status || "-"}
                  </span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}